'use client'

import { useState } from 'react'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useStore } from '@/store'
import { useAddCustomerToSegmentMutation } from '@/generated/graphql'
import { tracking } from '@/tracking'
import { handleError } from '@/AdvertisingModal/utils/errorHandling'
import { DELUXE_EDITION_SEGMENT_ID } from './constants'
import { playfair } from './fonts'
import { DeluxeSignupLoggedInMessage } from './DeluxeSignupLoggedInMessage'

type Props = {
  onSuccess: () => void
}

export function DeluxeSignupLoggedInBlock({ onSuccess }: Props) {
  const user = useStore(state => state.user)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const { mutateAsync: addToSegment } = useAddCustomerToSegmentMutation()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.id) return
    setFormErrors([])
    setSubmitting(true)
    try {
      await addToSegment({
        segmentId: DELUXE_EDITION_SEGMENT_ID,
      })
      tracking.identify(user.email, { customerId: user.id })
      tracking.track('Deluxe edition list signup', { source: 'deluxe-books' })
      onSuccess()
    } catch (err) {
      setFormErrors([
        handleError(
          err,
          undefined,
          'Something went wrong. Please try again in a moment.'
        ),
      ])
    } finally {
      setSubmitting(false)
    }
  }

  if (!user) return null

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="text-left max-w-lg mx-auto space-y-4"
    >
      <ErrorBox errors={formErrors} />
      <DeluxeSignupLoggedInMessage email={user.email} />
      <Button
        type="submit"
        disabled={submitting}
        className={`${playfair.className} w-full rounded-lg border-0 bg-gradient-to-br from-[#d4af37] to-[#b8941f] py-4 text-lg font-semibold uppercase tracking-wide text-white shadow-md transition hover:from-[#b8941f] hover:to-[#d4af37] hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0`}
      >
        {submitting ? 'Please wait…' : 'Request Exclusive Access'}
      </Button>
    </form>
  )
}
